/**
 * GUID/UUID utilities for generating and validating unique identifiers
 */

/**
 * Empty GUID constant representing a null/empty identifier
 */
export const EMPTY_GUID = '00000000-0000-0000-0000-000000000000'

/**
 * Generate a UUID v4 (random)
 * Implementation follows RFC 4122 version 4 UUID format
 * @returns A random UUID string in the format xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx
 */
export function generateUuid(): string {
  // Use crypto.randomUUID if available (Node.js 19+, modern browsers)
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID()
  }

  // Fallback implementation for older environments
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

/**
 * Check if a GUID string represents an empty/null GUID
 * @param guid - The GUID string to check
 * @returns true if the GUID is empty (all zeros), false otherwise
 */
export function isEmptyGuid(guid: string | undefined | null): boolean {
  return !guid || guid === EMPTY_GUID
}

/**
 * Check if a string is a valid GUID/UUID format
 * @param value - The string to validate
 * @returns true if the string is a valid GUID format
 */
export function isValidGuid(value: string | undefined | null): boolean {
  if (!value) return false
  const guidRegex =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
  return guidRegex.test(value)
}
